// src/components/analytics/ComparisonChart.js
import React, { useMemo } from 'react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Scatter, // Import Scatter for single point rendering
    ScatterChart // Import ScatterChart as fallback for single point
} from 'recharts';
import { METRICS } from '../../utils/analyticsUtils'; // Adjust path if needed

/**
 * Formats a numeric timestamp into a short representation (e.g., "Jan 24").
 * Defined outside the component for stability.
 */
const xAxisTickFormatter = (timestamp) => {
    if (typeof timestamp !== 'number' || !Number.isFinite(timestamp)) {
        return ''; // Return empty for invalid timestamps
    }
    try {
        // Format valid timestamp as "MMM D" (e.g., "Jan 24")
        return new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch (e) {
        console.error("Error formatting X-axis timestamp:", timestamp, e);
        return ''; // Return empty string on formatting error
    }
};


/**
 * Renders a line chart comparing current vs. previous period data using Recharts.
 * Handles different metric types for tooltip formatting and axis formatting.
 * Uses numeric timestamps on the X-axis with explicit domain calculation.
 * Sets a fixed Y-axis domain if all data values are null.
 * Now supports single data point visualization.
 *
 * @param {Array} chartData - Array of sanitized data points [{ date: DateObject, current: number|null, previous: number|null }, ...]
 * @param {string} metricName - The display name of the metric.
 * @param {string} selectedMetricKey - The key of the selected metric (e.g., 'avgDprime').
 */
const ComparisonChart = ({ chartData, metricName, selectedMetricKey }) => {

  // --- Find metric info early for checks and formatting ---
  const metricInfo = Object.values(METRICS).find(m => m.key === selectedMetricKey);
  const isAverageMetric = metricInfo?.aggregation === 'average';
  const valueFormatter = metricInfo?.formatter || ((value) => value);
  const isTimeMetric = selectedMetricKey === METRICS.TOTAL_PLAY_TIME.key;
  const isSessionCountMetric = selectedMetricKey === METRICS.NUM_SESSIONS.key;

  // --- Data Validity & Significance Checks ---
  const validDataPoints = useMemo(() => {
    // console.log("ComparisonChart received chartData:", chartData); // Debug: Log raw input
    return Array.isArray(chartData)
      ? chartData.map(d => ({
             ...d,
             timestamp: (d.date instanceof Date && !isNaN(d.date.getTime())) ? d.date.getTime() : null
         })).filter(d => d.timestamp !== null)
      : [];
  }, [chartData]);

  // Changed this to also allow single data point
  const hasSingleDataPoint = validDataPoints.length === 1;
  const isValidForRendering = validDataPoints.length >= 1;

  // Check if there's at least one NON-NULL, FINITE data point
  const hasAnyFiniteData = useMemo(() => {
     return isValidForRendering && validDataPoints.some(d =>
        (d.current !== null && Number.isFinite(d.current)) ||
        (d.previous !== null && Number.isFinite(d.previous))
     );
  }, [validDataPoints, isValidForRendering]);

  // Check if there's significant data (non-zero for sum/count, any finite for average)
  const hasSignificantData = useMemo(() => {
    return isValidForRendering && validDataPoints.some(d =>
        (d.current !== null && Number.isFinite(d.current) && (isAverageMetric || d.current !== 0)) ||
        (d.previous !== null && Number.isFinite(d.previous) && (isAverageMetric || d.previous !== 0))
    );
  }, [validDataPoints, isValidForRendering, isAverageMetric]);


  // --- Explicit X-Axis Domain Calculation ---
  const xAxisDomain = useMemo(() => {
    if (!isValidForRendering) {
      return ['auto', 'auto'];
    }
    const timestamps = validDataPoints.map(p => p.timestamp);
    const minTimestamp = Math.min(...timestamps);
    const maxTimestamp = Math.max(...timestamps);

    if (Number.isFinite(minTimestamp) && Number.isFinite(maxTimestamp)) {
      if (minTimestamp === maxTimestamp) {
        // For a single point or all points with same timestamp,
        // add padding on both sides to make the point visible
        const oneDay = 24 * 60 * 60 * 1000; // one day in milliseconds
        return [minTimestamp - oneDay, maxTimestamp + oneDay];
      } else {
        // Add slight padding to make endpoints more visible
        const range = maxTimestamp - minTimestamp;
        const padding = range * 0.05; // 5% padding on each side
        return [minTimestamp - padding, maxTimestamp + padding];
      }
    } else {
      console.warn("ComparisonChart: Could not determine valid min/max timestamps for X-Axis domain.");
      return ['auto', 'auto'];
    }
  }, [validDataPoints, isValidForRendering]);


  // --- Conditional Y-Axis Domain ---
  const yAxisDomain = useMemo(() => {
      if (!hasAnyFiniteData) {
          // If ALL data points are null, set a fixed default domain [0, 1]
          // to prevent Recharts from calculating domain based on undefined min/max.
          return [0, 1];
      } else {
          // Find all finite values for Y axis calculation
          const allValues = validDataPoints.flatMap(d => [
            (d.current !== null && Number.isFinite(d.current)) ? d.current : null,
            (d.previous !== null && Number.isFinite(d.previous)) ? d.previous : null
          ]).filter(val => val !== null);
          
          if (allValues.length > 0) {
            const minVal = Math.min(...allValues);
            const maxVal = Math.max(...allValues);
            
            // For a single point or all identical values, add padding
            if (minVal === maxVal) {
              const padding = Math.max(maxVal * 0.2, 1); // at least 1 unit padding or 20%
              return [Math.max(0, minVal - padding), maxVal + padding];
            }
          }
          
          // Default case with some padding at the bottom
          return [0, 'auto'];
      }
  }, [hasAnyFiniteData, validDataPoints]); // Dependency: Re-calculate if the presence of finite data changes


  // --- Render Placeholder if Data Invalid/Insufficient ---
  if (!isValidForRendering || !hasSignificantData) {
    const message = !isValidForRendering
        ? `Not enough valid data points (${validDataPoints.length}) to display.`
        : `No significant data to display for "${metricName}" in the selected periods.`;
    return (
      <div className="mb-8 p-4 bg-white rounded-lg border shadow-sm h-72 flex items-center justify-center text-center text-gray-500">
        {message}
      </div>
    );
  }


  // --- Formatter for Y-Axis Ticks ---
  // Use the metric's specific formatter ONLY if the metric is totalPlayTime, otherwise default numeric formatting
  const yAxisTickFormatter = isTimeMetric ? valueFormatter : undefined;


  // --- Render a Scatter Chart for Single Data Point ---
  if (hasSingleDataPoint) {
    const point = validDataPoints[0];
    const dataPoints = [];
    
    if (point.current !== null && Number.isFinite(point.current)) {
      dataPoints.push({ x: point.timestamp, y: point.current, name: 'Current Period' });
    }
    
    if (point.previous !== null && Number.isFinite(point.previous)) {
      dataPoints.push({ x: point.timestamp, y: point.previous, name: 'Previous Period' });
    }
    
    if (dataPoints.length === 0) {
      return (
        <div className="mb-8 p-4 bg-white rounded-lg border shadow-sm h-72 flex items-center justify-center text-center text-gray-500">
          No valid data for the single data point.
        </div>
      );
    }
    
    return (
      <div className="mb-8 p-4 bg-white rounded-lg border shadow-sm h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart
            margin={{ top: 20, right: 25, left: isTimeMetric ? 5 : -15, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            
            <XAxis 
              type="number"
              dataKey="x"
              name="Date"
              domain={xAxisDomain}
              tickFormatter={xAxisTickFormatter}
              tick={{ fontSize: 10 }}
            />
            
            <YAxis
              type="number"
              dataKey="y"
              name={metricName}
              domain={yAxisDomain}
              tickFormatter={yAxisTickFormatter}
              width={isTimeMetric ? 60 : 45}
              allowDecimals={!isSessionCountMetric && !isTimeMetric}
              tick={{ fontSize: 10 }}
            />
            
            <Tooltip
              formatter={(value, name, props) => {
                const formattedValue = valueFormatter(value);
                return [formattedValue, props.payload.name];
              }}
              labelFormatter={(label) => {
                try { 
                  return new Date(label).toLocaleDateString('en-US', { 
                    weekday: 'short', month: 'short', day: 'numeric' 
                  }); 
                } catch(e) { 
                  return 'Date'; 
                }
              }}
              contentStyle={{ 
                fontSize: '12px', 
                padding: '5px 8px', 
                borderRadius: '4px', 
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)' 
              }}
            />
            
            <Legend wrapperStyle={{ fontSize: "12px", paddingTop: '10px' }} />
            
            <Scatter 
              name="Current Period" 
              data={dataPoints.filter(p => p.name === 'Current Period')}
              fill="#4f46e5"
              shape="circle"
              r={6}  // Explicitly set radius to make points more visible
              strokeWidth={2}
              stroke="#fff"
            />
            
            <Scatter 
              name="Previous Period" 
              data={dataPoints.filter(p => p.name === 'Previous Period')}
              fill="#a0aec0"
              shape="circle" 
              r={6}  // Explicitly set radius to make points more visible
              strokeWidth={2}
              stroke="#fff"
            />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    );
  }


  // --- Recharts Implementation for Multiple Data Points ---
  return (
    <div className="mb-8 p-4 bg-white rounded-lg border shadow-sm h-72">
       <ResponsiveContainer width="100%" height="100%">
         <LineChart
           data={validDataPoints}
           margin={{ top: 5, right: 25, left: isTimeMetric ? 5 : -15, bottom: 5 }}
         >
           <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />

           {/* X-axis (Uses explicit domain) */}
           <XAxis
             allowDuplicatedCategory={false}
             dataKey="timestamp"
             scale="time"
             type="number"
             domain={xAxisDomain} // Use the calculated explicit domain
             tickFormatter={xAxisTickFormatter} // Use the formatter defined outside
             tick={{ fontSize: 10 }}
             name="Date"
           />

           {/* Y-axis (Uses conditional domain) */}
           <YAxis
             tick={{ fontSize: 10 }}
             tickFormatter={yAxisTickFormatter} // Apply specific time formatter if needed
             domain={yAxisDomain} // Use the conditional domain
             width={isTimeMetric ? 60 : 45} // Adjust width for time labels
             name={metricName}
             allowDecimals={!isSessionCountMetric && !isTimeMetric} // Allow decimals unless it's count or time
           />

           {/* Tooltip */}
           <Tooltip
             formatter={(value, name, props) => {
                 const formattedValue = (value === null || value === undefined || !Number.isFinite(value))
                     ? 'N/A'
                     : valueFormatter(value); // Use the metric-specific formatter
                 const seriesName = name === 'current' ? 'Current Period' : 'Previous Period';
                 return [formattedValue, seriesName];
             }}
             labelFormatter={(label, payload) => {
                 const pointData = payload?.[0]?.payload;
                 if (pointData?.date instanceof Date && !isNaN(pointData.date)) {
                     try { return pointData.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); }
                     catch (e) { console.error("Error formatting tooltip date label:", e); }
                 }
                 if (typeof label === 'number' && Number.isFinite(label)) {
                    try { return new Date(label).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); }
                    catch(e) { /* ignore */ }
                 }
                 return 'Date';
             }}
             contentStyle={{ fontSize: '12px', padding: '5px 8px', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
             itemStyle={{ padding: '2px 0' }}
             cursor={{ stroke: '#a0aec0', strokeWidth: 1, strokeDasharray: '3 3' }}
           />

           {/* Legend */}
           <Legend wrapperStyle={{ fontSize: "12px", paddingTop: '10px' }} />

           {/* Lines */}
           <Line 
             isAnimationActive={false} 
             type="monotone" 
             dataKey="previous" 
             stroke="#a0aec0" 
             strokeWidth={2} 
             name="Previous Period" 
             dot={{ r: 5, fill: "#a0aec0", strokeWidth: 2, stroke: '#fff' }} 
             activeDot={{ r: 7, fill: "#a0aec0", stroke: '#fff', strokeWidth: 2 }}
             connectNulls={false} 
           />
           
           <Line 
             isAnimationActive={false} 
             type="monotone" 
             dataKey="current" 
             stroke="#4f46e5" 
             strokeWidth={2} 
             name="Current Period" 
             dot={{ r: 5, fill: "#4f46e5", strokeWidth: 2, stroke: '#fff' }} 
             activeDot={{ r: 7, fill: "#4f46e5", stroke: '#fff', strokeWidth: 2 }}
             connectNulls={false} 
           />

         </LineChart>
       </ResponsiveContainer>
    </div>
  );
};

export default ComparisonChart;